import React, { FC, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Cookies from 'js-cookie';

import * as CSS from './navigation-styles';
import { FlexRowCentred } from '../common-styles/common-styles';
import { TournamentHistory } from '../../../database.schema';
import Tick from '../svg/Tick';
import { isLoggedIn } from '../../app/authentication/auth';
import { useFetchTournaments } from '../../api/hooks/use-fetch-tournaments';
import { SelectedIndexContext } from '../../App';
import { RYDER_CUP_TYPE, STABLEFORD_WITH_TEAM_GAMES, TOURNAMENT_STATE_ARCHIVED, TOURNAMENT_STATE_IN_PLAY } from '../../app/constants';

const getTournamentHistoryItems = (tournaments: TournamentHistory[], selectedIndex: number, setShowNav: (value: boolean) => void): JSX.Element | null => {
  if (tournaments && tournaments.length > 1) {
    return (
      <div>
        <ListItemBreak />
        {tournaments.map((tournament, index) => {
          return (
            <li key={`tournaments-${tournament.index}`} data-testid="location" onClick={() => setShowNav(false)}>
              <Link to={`previous-tour/${index}`}>
                {tournament.index === selectedIndex ? (
                  <div>
                    {tournament.year} <Tick width="25px" color="white" />
                  </div>
                ) : (
                  `${tournament.year}`
                )}
              </Link>
            </li>
          );
        })}
      </div>
    );
  }
  return null;
};

export const ListItemBreak = styled.li`
  list-style-type: none;
  margin-top: 1rem;
  margin-bottom: 1rem;
  border-top: solid 1px white;
`;

export const Navigation: FC = () => {
  const userIsAdmin = isLoggedIn();
  const [showNav, setShowNav] = useState(false);
  const { data } = useFetchTournaments();

  const { selectedTournamentIndex, setSelectedTournamentIndex } = useContext(SelectedIndexContext);

  const tournaments = data as TournamentHistory[];
  return (
    <CSS.NavigationWrapper data-testid="nav wrapper">
      <CSS.MenuFred
        data-testid="hamburger-fred"
        style={showNav ? { height: '100%', left: '0', backgroundColor: 'rgba(1, 81, 145, 1)' } : { height: '3rem', left: '-16rem', backgroundColor: 'transparent' }}
      >
        <CSS.HamburgerContainer onClick={() => setShowNav(!showNav)}>
          <FlexRowCentred>
            <CSS.HamburgerPrefixText>MENU</CSS.HamburgerPrefixText>
            <div data-testid="hamburger">
              <CSS.HamburgerBar1
                data-testid="bar1"
                style={showNav ? { transform: 'translate(2px, 8px) rotate(-45deg)', backgroundColor: 'white' } : { transform: 'none' }}
              ></CSS.HamburgerBar1>
              <CSS.HamburgerBar2 data-testid="bar2" style={showNav ? { opacity: 0 } : { opacity: 1 }}></CSS.HamburgerBar2>
              <CSS.HamburgerBar1
                data-testid="bar3"
                style={showNav ? { transform: 'translate(2px, -11px) rotate(45deg)', backgroundColor: 'white' } : { transform: 'none' }}
              ></CSS.HamburgerBar1>
            </div>
          </FlexRowCentred>
        </CSS.HamburgerContainer>
        <CSS.MenuUl data-testid="menu-ul" style={showNav ? { display: 'block', background: 'inherit' } : { display: 'none' }}>
          <li data-testid="location" onClick={() => setShowNav(!showNav)}>
            <Link to="/">HOME</Link>
          </li>
          {getTournamentHistoryItems(tournaments, selectedTournamentIndex, setShowNav)}
          <ListItemBreak />
          {userIsAdmin && tournaments && selectedTournamentIndex === tournaments.length - 1 && tournaments[selectedTournamentIndex].type === RYDER_CUP_TYPE && (
            <li>
              <Link to="/add-results" data-testid="add-results" onClick={() => setShowNav(!showNav)}>
                ADD RESULTS
              </Link>
            </li>
          )}
          {userIsAdmin && tournaments && selectedTournamentIndex === tournaments.length - 1 && tournaments[selectedTournamentIndex].type === STABLEFORD_WITH_TEAM_GAMES && (
            <li>
              <Link to="/add-team-game-results" data-testid="team-game-add-results" onClick={() => setShowNav(!showNav)}>
                ADD RESULTS
              </Link>
            </li>
          )}
          {userIsAdmin && (
            <li>
              <Link data-testid="course-admin" to="/course-admin" onClick={() => setShowNav(!showNav)}>
                COURSE ADMIN
              </Link>
            </li>
          )}
          {userIsAdmin && (
            <li>
              <Link data-testid="player-admin" to="/player-admin" onClick={() => setShowNav(!showNav)}>
                PLAYER ADMIN
              </Link>
            </li>
          )}
          {userIsAdmin && (
            <li>
              <Link data-testid="team-admin" to="/team-admin" onClick={() => setShowNav(!showNav)}>
                TEAM NAME ADMIN
              </Link>
            </li>
          )}
          {userIsAdmin && (
            <li>
              <Link data-testid="tee-booking-admin" to="/tee-booking-admin" onClick={() => setShowNav(!showNav)}>
                TEE BOOKING ADMIN
              </Link>
            </li>
          )}
          {userIsAdmin && tournaments && tournaments.every((tournament) => tournament.state === TOURNAMENT_STATE_ARCHIVED) && (
            <li>
              <Link data-testid="create-tournament" to={`/create-tournament/${new Date().getFullYear()}`} onClick={() => setShowNav(!showNav)}>
                CREATE TOURNAMENT
              </Link>
            </li>
          )}
          {userIsAdmin && tournaments && tournaments.some((tournament) => tournament.state === TOURNAMENT_STATE_IN_PLAY) && (
            <li>
              <Link data-testid="archive-tournament" to="archive-tournament" onClick={() => setShowNav(!showNav)}>
                ARCHIVE TOURNAMENT
              </Link>
            </li>
          )}
          {userIsAdmin && (
            <li>
              <button
                style={{ border: 'none', color: 'white', background: 'transparent', fontSize: '16px' }}
                data-testid="log-out"
                onClick={() => {
                  const tourGroup = Cookies.get('tourGroup');
                  tourGroup === 'ibis' ? Cookies.set('tourGroup', 'sbm') : Cookies.set('tourGroup', 'ibis');
                  window.location.reload();
                }}
              >
                TOGGLE TOUR GROUP
              </button>
            </li>
          )}
          {userIsAdmin && <ListItemBreak />}
          {userIsAdmin && (
            <li>
              <button
                style={{ border: 'none', color: 'white', background: 'transparent', fontSize: '16px' }}
                data-testid="log-out"
                onClick={() => {
                  Cookies.remove('token');
                  setShowNav(false);
                }}
              >
                LOG OUT
              </button>
            </li>
          )}
          {!userIsAdmin && (
            <li>
              <Link data-testid="login" to="/login" onClick={() => setShowNav(!showNav)}>
                LOGIN
              </Link>
            </li>
          )}
        </CSS.MenuUl>
      </CSS.MenuFred>
    </CSS.NavigationWrapper>
  );
};
